'use client';

import { c as _c } from "react/compiler-runtime";
import './nav-block.scss';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import type { FlexibleContentProps } from '@sola/schema/lib/schema.ts';
import { useListArticlesQuery } from '@sola/skippy-search-sdk/lib/queries.ts';
import type { PublishedEntryResponseDto } from '@sola/skippy-search-sdk/lib/schema.ts';
import { NavCardSkeleton } from '../nav-card/nav-card-skeleton.tsx';
import { NavCard } from '../nav-card/nav-card.tsx';
type NavBlockItemsProps = {
  featuredArticles: true;
  items?: never;
} | {
  featuredArticles?: never;
  items: FlexibleContentProps[];
};
type NavBlockProps = NavBlockItemsProps & {
  title?: string;
  maxItems?: number;
  mobile?: boolean;
};
const toNavCardProps = (item: PublishedEntryResponseDto, linkText: string): FlexibleContentProps => ({
  title: item.pageTitle || undefined,
  body: item.summary ? <p>{item.summary}</p> : undefined,
  media: item.media ? {
    image: {
      url: item.media.image?.url || '',
      alt: item.media.image?.alt || '',
      height: 96,
      width: 70
    }
  } : undefined,
  primaryCTA: item.path ? {
    title: linkText,
    link: item.path
  } : undefined
});
const FeaturedArticlesContent = t0 => {
  const $ = _c(13);
  const {
    maxItems: t1,
    mobile: t2
  } = t0;
  const maxItems = t1 === undefined ? 1 : t1;
  const mobile = t2 === undefined ? false : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["articles"];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    t
  } = useTranslation(t3);
  let t4;
  if ($[1] !== maxItems) {
    t4 = {
      promoted: true,
      perPage: maxItems,
      sort: "datePublished",
      sortDir: "desc"
    };
    $[1] = maxItems;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    data,
    isLoading
  } = useListArticlesQuery(t4);
  if (isLoading) {
    let t5;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <NavCardSkeleton />;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    return t5;
  }
  if (!data?.items?.length) {
    return null;
  }
  let t5;
  if ($[4] !== data.items || $[5] !== mobile || $[6] !== t) {
    let t6;
    if ($[8] !== mobile || $[9] !== t) {
      t6 = item => <NavCard key={item.contentfulEntryID} {...toNavCardProps(item, t("articles:readMore"))} mobile={mobile} featured={true} />;
      $[8] = mobile;
      $[9] = t;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    t5 = data.items.map(t6);
    $[4] = data.items;
    $[5] = mobile;
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <div className="NavBlock__featuredArticles flex flex-col lg:gap-snug-xl items-start">{t5}</div>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
export const NavBlock: React.FC<NavBlockProps> = props => {
  const $ = _c(20);
  const {
    title,
    maxItems,
    mobile: t0
  } = props;
  const mobile = t0 === undefined ? false : t0;
  if ("items" in props && props.items) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = cn("NavBlock");
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== title) {
      t2 = title && <span className="NavBlock__title label label-lg label-potent">{title}</span>;
      $[1] = title;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== mobile || $[4] !== props.items) {
      let t4;
      if ($[6] !== mobile) {
        t4 = (item, idx) => <NavCard key={item.title || idx} {...item} mobile={mobile} />;
        $[6] = mobile;
        $[7] = t4;
      } else {
        t4 = $[7];
      }
      t3 = props.items.map(t4);
      $[3] = mobile;
      $[4] = props.items;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[8] !== t2 || $[9] !== t3) {
      t4 = <div className={t1}>{t2}{t3}</div>;
      $[8] = t2;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  let t1;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("NavBlock");
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== title) {
    t2 = title && <span className="NavBlock__title label label-lg label-potent">{title}</span>;
    $[12] = title;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== maxItems || $[15] !== mobile) {
    t3 = <FeaturedArticlesContent maxItems={maxItems} mobile={mobile} />;
    $[14] = maxItems;
    $[15] = mobile;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== t2 || $[18] !== t3) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return t4;
};